import {
  checkIsAreaPageLocked,
  checkIsFormPageLocked,
  checkIsHiddenField,
  stripHiddenPrefix,
} from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { selectLoanApplication } from './loanApplication.selectors';

export const selectFormLockedFields = memoizeOne(formName =>
  createSelector(selectLoanApplication, ({ lockedFields }) => {
    if (!formName) {
      return [];
    }

    const { lockedFieldIds } = lockedFields;

    return lockedFieldIds
      .filter(field => !checkIsHiddenField(field) && field.includes(formName))
      .map(field => field.split('-').pop());
  }),
);

export const selectFormLockedFieldsMap = memoizeOne(formName =>
  createSelector(selectFormLockedFields(formName), lockedFields => {
    return lockedFields.reduce((acc, field) => {
      acc[field] = true;

      return acc;
    }, {});
  }),
);

export const selectIsActionLocked = memoizeOne(actionName =>
  createSelector(selectLoanApplication, ({ lockedFields }) => {
    const { lockedFieldIds } = lockedFields;

    return lockedFieldIds.some(
      field => field.startsWith('actions-') && field.endsWith(`-${actionName}`),
    );
  }),
);

export const selectFormLockedPage = memoizeOne(formName =>
  createSelector(selectLoanApplication, ({ lockedFields }) =>
    checkIsFormPageLocked({ formName, pageIds: lockedFields.lockedPageIds }),
  ),
);

export const selectFormHiddenPage = memoizeOne(formName =>
  createSelector(selectLoanApplication, ({ lockedFields }) => {
    if (!formName) {
      return false;
    }

    const hiddenPageIds = lockedFields.lockedPageIds.filter(checkIsHiddenField);

    return checkIsFormPageLocked({ formName, pageIds: hiddenPageIds });
  }),
);

export const selectHiddenPageIds = createSelector(selectLoanApplication, ({ lockedFields }) => {
  const { lockedPageIds } = lockedFields;

  return lockedPageIds.filter(checkIsHiddenField).map(stripHiddenPrefix);
});

export const selectHiddenFieldIds = createSelector(selectLoanApplication, ({ lockedFields }) => {
  const { lockedFieldIds } = lockedFields;

  return lockedFieldIds.filter(checkIsHiddenField).map(stripHiddenPrefix);
});

export const selectLockedFieldIds = createSelector(selectLoanApplication, ({ lockedFields }) => {
  const { lockedFieldIds } = lockedFields;

  return lockedFieldIds.filter(field => !checkIsHiddenField(field));
});

export const selectFormHiddenFields = memoizeOne(formName =>
  createSelector(selectLoanApplication, ({ lockedFields }) => {
    if (!formName) {
      return [];
    }

    const { lockedFieldIds } = lockedFields;

    return lockedFieldIds
      .filter(field => checkIsHiddenField(field) && stripHiddenPrefix(field).includes(formName))
      .map(field => stripHiddenPrefix(field).split('-').pop());
  }),
);

export const selectFormHiddenFieldsMap = memoizeOne(formName =>
  createSelector(selectFormHiddenFields(formName), hiddenFields => {
    return hiddenFields.reduce((acc, field) => {
      acc[field] = true;

      return acc;
    }, {});
  }),
);

export const selectIsAreaHidden = memoizeOne((areaName, levelType) =>
  createSelector(selectLoanApplication, ({ lockedFields }) =>
    checkIsAreaPageLocked({
      areaName,
      levelType,
      pageIds: lockedFields.lockedPageIds,
      hiddenOnly: true,
    }),
  ),
);

export const selectIsAreaLocked = memoizeOne((areaName, levelType) =>
  createSelector(selectLoanApplication, ({ lockedFields }) =>
    checkIsAreaPageLocked({
      areaName,
      levelType,
      pageIds: lockedFields.lockedPageIds,
    }),
  ),
);
